import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";
import { Chip, IconButton, Tooltip } from "@mui/material";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridLinkColumn,
  GridColumns,
  PageHeader,
} from "common";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import moment from "moment";
import { useState } from "react";
import BulkEmailSentPreviewDialog from "./BulkEmailSentPreviewDialog";
import BulkEmailSentView from "./BulkEmailSentView";
import { BulkEmailSentItem } from "./bulkEmailSentApi";
import useBulkEmailSentList from "./useBulkEmailSentList";

export interface BulkEmailSentListProps extends Omit<
  DataGridBaseBaseProps,
  "id"
> {
  showHeader?: boolean;
}

export default function BulkEmailSentList({
  showHeader = true,
  ...props
}: BulkEmailSentListProps) {
  const { params, scope, editUrl, ...service } = useBulkEmailSentList();
  const { hasPermissions } = useAccess();
  const excelUrl = useExcelUrl("bulk-emails-sent");
  const [previewItem, setPreviewItem] = useState<BulkEmailSentItem | null>(
    null,
  );

  let columns: GridColumns<BulkEmailSentItem> = [
    {
      field: "booking_number",
      headerName: I18n.t("BulkEmailSent.attributes.booking_number"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`/bookings/${params.row?.booking_id}`}
          params={params}
        />
      ),
    },
    {
      field: "start_time",
      headerName: I18n.t("BulkEmailSent.attributes.start_time"),
      type: "datetime",
      renderCell: params =>
        params?.row?.start_time
          ? moment(params?.row?.start_time).format("L LT")
          : null,
    },
    {
      field: "program_name",
      headerName: I18n.t("BulkEmailSent.attributes.program_name"),
    },
    {
      field: "booking_name",
      headerName: I18n.t("BulkEmailSent.attributes.booking_name"),
    },
    {
      field: "template_name",
      headerName: I18n.t("BulkEmailSent.attributes.template_name"),
    },
    {
      field: "email",
      headerName: I18n.t("BulkEmailSent.attributes.email"),
      flex: 1,
    },
    {
      field: "sent_at",
      headerName: I18n.t("BulkEmailSent.attributes.sent_at"),
      type: "datetime",
      renderCell: params =>
        params?.row?.sent_at
          ? moment(params?.row?.sent_at).format("L LT")
          : null,
    },
    {
      field: "queue_name",
      headerName: I18n.t("BulkEmailSent.attributes.queue_name"),
    },
    {
      field: "error_code",
      headerName: I18n.t("BulkEmailSent.attributes.error_code"),
      renderCell: params => {
        const errorCode = params.row?.error_code;
        if (errorCode === 0) {
          return <Chip size="small" label="OK" color="success" />;
        }
        return <Chip size="small" label={errorCode} color="error" />;
      },
    },
    {
      field: "error_message",
      headerName: I18n.t("BulkEmailSent.attributes.error_message"),
      flex: 1,
    },
    {
      field: "created_at",
      headerName: I18n.t("App.created_at"),
      type: "datetime",
      hideable: true,
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<SendIcon />}
          title={I18n.t("BulkEmailSent.title")}
        />
      )}
      <DataGrid
        {...service}
        columns={columns}
        id="bulk-emails-sent"
        excelUrl={excelUrl}
        {...props}
        canCreate={false}
        canDelete={hasPermissions([
          "bulk_email_sent.editany",
          "bulk_email_sent.editown",
        ])}
        canEdit={false}
        canView={true}
        rowActions={params =>
          params.row.booking_id && params.row.email_template_id
            ? [
                <Tooltip
                  key="email-preview"
                  title={I18n.t("BulkEmailSent.previewAction")}
                >
                  <IconButton
                    size="small"
                    aria-label={I18n.t("BulkEmailSent.previewAction")}
                    onClick={() => setPreviewItem(params.row)}
                  >
                    <EmailOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>,
              ]
            : []
        }
        renderModalForm={props => <BulkEmailSentView {...props} />}
        modalProps={{ maxWidth: "sm" }}
        canBatchActions={hasPermissions([
          "bulk_email_sent.editany",
          "bulk_email_sent.editown",
        ])}
        canHandleTrash={hasPermissions([
          "bulk_email_sent.editany",
          "bulk_email_sent.editown",
        ])}
      />
      <BulkEmailSentPreviewDialog
        item={previewItem}
        onClose={() => setPreviewItem(null)}
      />
    </>
  );
}
